import { useState } from 'react';
import { FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useRecoilValue } from 'recoil';
import {
  ExpandableContentButton,
  ExpandableContentWrapper,
  InfoButton,
  SGSelect,
} from 'components/shared';
import { CanvasGroupLinkButton } from 'components/canvas/groups';
import { CompositeViewGraph } from 'components/equityhub/EquityGraph/CompositeViewGraph';
import { CompositeViewProvider } from 'components/equityhub/EquityGraph/CompositeViewProvider';
import { useCompositeViewCtx } from 'components/equityhub/EquityGraph/useCompositeViewCtx';
import { equitySymbolsState } from 'states';
import { CanvasContentId, CanvasComponentConfig } from 'types/canvas';
import {
  CompositeViewConfig,
  DEFAULT_COMPOSITE_VIEW_CONFIG,
  EhCompositeMode,
} from 'types';
import CanvasSymSelector from './CanvasSymSelector';

// Mirrors the labels EquityHub shows on the same selector so users see
// the same terminology in both places.
const COMPOSITE_MODE_LABELS = new Map<EhCompositeMode, string>([
  [EhCompositeMode.Gamma, 'Gamma'],
  [EhCompositeMode.Factors, 'Impact'],
]);

const compositeModeLabel = (value: EhCompositeMode) =>
  COMPOSITE_MODE_LABELS.get(value) ?? '';

interface CanvasCompositeViewProps {
  id: number;
  sym: string;
  config: CanvasComponentConfig;
  onSymChange: (sym: string) => void;
  onConfigChange: (patch: Partial<CanvasComponentConfig>) => void;
  /** Renders the Synthetic-OI variant when true; Total-OI otherwise. */
  isSynthOI?: boolean;
}

const KeyLevelsToggle = () => {
  const { config, updateConfig } = useCompositeViewCtx();
  return (
    <FormControlLabel
      className="canvas-no-drag"
      sx={{ ml: 0, whiteSpace: 'nowrap' }}
      control={
        <Switch
          size="small"
          checked={config.showKeyLevels}
          onChange={(_e, checked) => updateConfig({ showKeyLevels: checked })}
        />
      }
      label={<Typography sx={{ fontSize: 11 }}>Key levels</Typography>}
      labelPlacement="end"
    />
  );
};

/**
 * Synth-OI Composite View has two distinct chart variants: a gradient
 * gamma curve (Gamma) vs the position/activity-factor shading (Impact).
 * Mirrors the same selector EquityHub shows for this chart so users get
 * identical controls in both places.
 */
const CompositeModeSelector = () => {
  const { config, updateConfig } = useCompositeViewCtx();
  return (
    <SGSelect
      label="Chart Type"
      value={config.compositeMode}
      setter={(val: EhCompositeMode) => updateConfig({ compositeMode: val })}
      options={[EhCompositeMode.Gamma, EhCompositeMode.Factors]}
      multiple={false}
      optionsTextTransform={compositeModeLabel}
      sx={{ minWidth: 110, fontSize: '0.8rem' }}
    />
  );
};

const CanvasCompositeView = ({
  id,
  sym,
  config: canvasConfig,
  onSymChange,
  onConfigChange,
  isSynthOI = false,
}: CanvasCompositeViewProps) => {
  const ehSymbols = useRecoilValue(equitySymbolsState);
  const symOptions = ehSymbols.map((s) => ({ symbol: s.symbol, name: s.name }));

  const [localConfig, setLocalConfig] = useState<CompositeViewConfig>(() => ({
    ...DEFAULT_COMPOSITE_VIEW_CONFIG,
    ...(canvasConfig as Partial<CompositeViewConfig>),
  }));

  const handleConfigChange = (patch: Partial<CompositeViewConfig>) => {
    setLocalConfig((prev) => {
      const next = { ...prev, ...patch };
      onConfigChange(patch);
      return next;
    });
  };

  const contentId = isSynthOI
    ? CanvasContentId.SYNTH_COMPOSITE_VIEW_CHART
    : CanvasContentId.COMPOSITE_VIEW_CHART;
  const expandableContentButtonType = `${contentId}-${id}`;

  return (
    <CompositeViewProvider
      selectedSym={sym}
      isSynthOI={isSynthOI}
      config={localConfig}
      onConfigChange={handleConfigChange}
    >
      <ExpandableContentWrapper
        type={expandableContentButtonType}
        styleOverrides={{ p: 1, height: '100%' }}
      >
        <Stack
          direction="column"
          spacing={1}
          sx={{ height: '100%', width: '100%' }}
        >
          <Stack
            sx={{
              px: 1,
              py: 0.5,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.5,
            }}
          >
            <CanvasSymSelector
              sym={sym}
              options={symOptions}
              onSymChange={onSymChange}
            />
            <KeyLevelsToggle />
            {isSynthOI && <CompositeModeSelector />}
            <InfoButton articleKey="composite" />
            <CanvasGroupLinkButton componentId={id} contentId={contentId} />
            <ExpandableContentButton type={expandableContentButtonType} />
          </Stack>
          <CompositeViewGraph />
        </Stack>
      </ExpandableContentWrapper>
    </CompositeViewProvider>
  );
};

export default CanvasCompositeView;
